import type { ReactNode } from "react";

/**
 * Inline message banner (2026-09): the one way to show an error, warning,
 * success, or informational note inside a page. Tint border, a 40% tint fill so
 * the message sits on near-white, and the tone's AA text shade. Errors announce
 * themselves (role="alert"); the rest are plain status.
 *
 * This replaced the ad-hoc banners built from Tailwind's stock red palette, which
 * measured 4.41:1 and sat outside the contrast script.
 */
export type BannerTone = "error" | "warning" | "success" | "info";

const TONES: Record<BannerTone, string> = {
  error: "border-spark-red-tint bg-spark-red-tint/40 text-spark-red-text",
  warning: "border-spark-yellow-tint bg-spark-yellow-tint/40 text-spark-yellow-text",
  success: "border-spark-green-tint bg-spark-green-tint/40 text-spark-green-text",
  info: "border-spark-blue-tint bg-spark-blue-tint/40 text-spark-blue-text",
};

const SIZES = {
  sm: "px-3 py-2 text-xs",
  md: "px-4 py-3 text-sm",
} as const;

export function Banner({
  tone = "info",
  size = "md",
  className,
  children,
  ...rest
}: {
  tone?: BannerTone;
  size?: keyof typeof SIZES;
  className?: string;
  children: ReactNode;
  id?: string;
}) {
  return (
    <div
      role={tone === "error" ? "alert" : "status"}
      className={`rounded-md border ${TONES[tone]} ${SIZES[size]} ${className ?? ""}`}
      {...rest}
    >
      {children}
    </div>
  );
}
